import React from 'react';
import { X, Minimize2, Sparkles, Volume2, Pause, Loader, AlertCircle, ExternalLink } from 'lucide-react';
import { useAIAdvisor } from '../../contexts/AIAdvisorContext';
import { useNavigate, useLocation } from 'react-router-dom';
import './PersistentAIPlayer.css';

// Section to page mapping with section IDs for scrolling
const SECTION_PAGES = {
  'Name': { page: '/about-me', label: 'Profile', sectionId: 'personal-information' },
  'Profile Image': { page: '/about-me', label: 'Profile', sectionId: 'personal-information' },
  'Bio': { page: '/about-me', label: 'Profile', sectionId: 'personal-information' },
  'Bio Video': { page: '/about-me', label: 'Profile', sectionId: 'bio-video' },
  'Video Gallery': { page: '/about-me', label: 'Profile', sectionId: 'featured-videos' },
  'Contact': { page: '/about-me', label: 'Profile', sectionId: 'personal-information' },
  'Address': { page: '/about-me', label: 'Profile', sectionId: 'personal-information' },
  'Interests': { page: '/about-me', label: 'Profile', sectionId: 'interests' },
  'Work Experience': { page: '/education', label: 'Education', sectionId: 'work-experience' },
  'Previous Employment': { page: '/about-me', label: 'Profile', sectionId: 'previous-employment' },
  'Education (Current Courses)': { page: '/education', label: 'Education', sectionId: 'currently-studying' },
  'Previous Qualifications': { page: '/education', label: 'Education', sectionId: 'existing-qualifications' },
  'Transferable Skills': { page: '/about-me', label: 'Profile', sectionId: 'transferable-skills' },
  'Trips & Experiences': { page: '/about-me', label: 'Profile', sectionId: 'experiences' },
  'Presentation & Awards': { page: '/about-me', label: 'Profile', sectionId: 'awards' },
  'Community & Charity': { page: '/about-me', label: 'Profile', sectionId: 'community-work' },
  'References': { page: '/about-me', label: 'Profile', sectionId: 'references' },
};

const SectionItem = ({ sectionName, isExpanded, onToggle, advice, audioLoading, audioPlaying, onToggleAudio, loading, isComplete = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const pageInfo = SECTION_PAGES[sectionName] || { page: '/about-me', label: 'Profile', sectionId: 'profile' };

  const handleNavigate = (e) => {
    e.stopPropagation();

    const currentPath = location.pathname;
    const targetPage = pageInfo.page;
    const sectionId = pageInfo.sectionId;

    if (currentPath === targetPage) {
      const selectors = [
        `#${sectionId}`,
        `[data-section="${sectionId}"]`,
        `[id*="${sectionId}"]`,
        `.${sectionId}`,
      ];

      let element = null;
      for (const selector of selectors) {
        try {
          element = document.querySelector(selector);
          if (element) break;
        } catch (err) {
          // Ignore invalid selector errors
        }
      }

      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        const originalBg = element.style.backgroundColor;
        element.style.transition = 'background-color 0.3s';
        element.style.backgroundColor = 'rgba(var(--primary-color-rgb, 59, 130, 246), 0.1)';
        setTimeout(() => {
          element.style.backgroundColor = originalBg;
        }, 2000);
      }
    } else {
      navigate(targetPage);
    }
  };

  return (
    <li style={{ marginBottom: '4px' }}>
      <div style={{
        padding: '8px 12px',
        borderRadius: '6px',
        backgroundColor: isExpanded ? 'rgba(0,0,0,0.05)' : 'transparent',
        transition: 'background-color 0.2s',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      }}>
        <span style={{ flex: 1, fontSize: '13px' }}>{sectionName}</span>
        {!isComplete && (
          <button
            onClick={onToggle}
            style={{
              padding: '4px 10px',
              fontSize: '11px',
              fontWeight: '600',
              backgroundColor: isExpanded ? '#6b7280' : 'var(--primary-color)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s',
            }}
            onMouseOver={(e) => { e.currentTarget.style.filter = 'brightness(0.9)'; }}
            onMouseOut={(e) => { e.currentTarget.style.filter = 'brightness(1)'; }}
          >
            {isExpanded ? 'Hide Tips' : 'Get Tips'}
          </button>
        )}
      </div>

      {isExpanded && (
        <div
          className="ai-player-section-tip"
          style={{
            marginTop: '8px',
            marginLeft: '22px',
            padding: '12px',
            borderRadius: '6px',
            borderLeft: '3px solid var(--primary-color)',
          }}
        >
          {loading || !advice ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#6b7280', fontSize: '13px' }}>
              <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} />
              Loading tips...
            </div>
          ) : (
            <>
              <p className="ai-player-section-tip" style={{ margin: '0 0 12px 0', fontSize: '13px', lineHeight: '1.6' }}>
                {advice}
              </p>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {audioLoading ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#6b7280' }}>
                    <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} />
                    Generating voice...
                  </div>
                ) : (
                  <button
                    onClick={(e) => { e.stopPropagation(); onToggleAudio(); }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '6px 10px',
                      backgroundColor: audioPlaying ? '#ef4444' : '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '600',
                    }}
                  >
                    {audioPlaying ? <><Pause size={12} /> Pause</> : <><Volume2 size={12} /> Play</>}
                  </button>
                )}
                <button className="ai-player-goto-btn" onClick={handleNavigate}>
                  Go to {pageInfo.label} <ExternalLink size={12} />
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </li>
  );
};

const PersistentAIPlayer = () => {
  const {
    isOpen,
    isMinimized,
    analysis,
    loading,
    audioLoading,
    isPlaying,
    error,
    closeAdvisor,
    minimizeAdvisor,
    maximizeAdvisor,
    getAdvice,
    toggleAudio,
    expandedSection,
    sectionAdvice,
    sectionLoading,
    sectionAudioLoading,
    sectionAudioPlaying,
    toggleSection,
    toggleSectionAudio,
  } = useAIAdvisor();

  if (!isOpen) return null;

  // Minimized view (sticky at bottom-right)
  if (isMinimized) {
    return (
      <div
        onClick={maximizeAdvisor}
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          backgroundColor: 'var(--primary-color)',
          color: 'white',
          padding: '16px 20px',
          borderRadius: '50px',
          boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          zIndex: 9999,
          transition: 'all 0.3s ease',
        }}
        onMouseOver={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; }}
        onMouseOut={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
      >
        <Sparkles size={20} />
        <span style={{ fontWeight: '600', fontSize: '14px' }}>
          {isPlaying ? '🔊 AI Playing...' : 'AI Career Advisor'}
        </span>
        {isPlaying && (
          <div style={{
            width: '8px',
            height: '8px',
            backgroundColor: '#10b981',
            borderRadius: '50%',
            animation: 'pulse 2s infinite'
          }} />
        )}
      </div>
    );
  }

  // Full expanded view (floating modal)
  return (
    <div
      className="ai-advisor-modal"
      style={{
        position: 'fixed',
        top: '80px',
        right: '20px',
        width: 'min(500px, calc(100vw - 40px))',
        maxHeight: 'calc(100vh - 100px)',
        borderRadius: '16px',
        boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div style={{
        padding: '16px 20px',
        backgroundColor: 'var(--primary-color)',
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '2px solid rgba(255,255,255,0.2)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Sparkles size={20} />
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
            AI Career Advisor
          </h3>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={minimizeAdvisor}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '6px',
              padding: '6px',
              cursor: 'pointer',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
            }}
            title="Minimize"
          >
            <Minimize2 size={16} />
          </button>
          <button
            onClick={closeAdvisor}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '6px',
              padding: '6px',
              cursor: 'pointer',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
            }}
            title="Close"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div
        className="ai-player-content"
        style={{ padding: '20px', overflowY: 'auto', flex: 1 }}
      >
        {/* Get Advice Button (if no analysis yet) */}
        {!analysis && !loading && (
          <button
            onClick={getAdvice}
            disabled={loading}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '14px 24px',
              backgroundColor: 'var(--primary-color)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '15px',
              fontWeight: '600',
              transition: 'all 0.2s',
              marginBottom: '16px',
            }}
            onMouseOver={(e) => { e.currentTarget.style.filter = 'brightness(0.9)'; }}
            onMouseOut={(e) => { e.currentTarget.style.filter = 'brightness(1)'; }}
          >
            <Sparkles size={18} />
            Analyse My Profile
          </button>
        )}

        {/* Loading State */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <Loader size={40} style={{
              animation: 'spin 1s linear infinite',
              color: 'var(--primary-color)',
              margin: '0 auto 16px'
            }} />
            <p className="ai-player-loading-text" style={{ fontSize: '14px', margin: 0 }}>
              AI is analysing your profile...
            </p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div
            className="ai-player-error"
            style={{
              padding: '12px 16px',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px',
              marginBottom: '16px',
            }}
          >
            <AlertCircle size={18} style={{ color: '#ef4444', marginTop: '2px', flexShrink: 0 }} />
            <p style={{ margin: 0, fontSize: '13px' }}>{error}</p>
          </div>
        )}

        {/* Analysis Results */}
        {analysis && (
          <div>
            {/* Completeness Bar */}
            <div
              className="ai-player-card"
              style={{
                marginBottom: '16px',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span className="ai-player-completeness-label">Profile Completeness</span>
                <span style={{ fontSize: '14px', fontWeight: '700', color: 'var(--primary-color)' }}>
                  {analysis.completeness}%
                </span>
              </div>
              <div className="ai-player-progress-track">
                <div style={{
                  width: `${analysis.completeness}%`,
                  height: '100%',
                  backgroundColor: analysis.completeness > 75 ? '#10b981' : analysis.completeness > 50 ? '#f59e0b' : '#ef4444',
                  transition: 'width 0.5s ease',
                }} />
              </div>
            </div>

            {/* AI Advice with Audio */}
            <div
              className="ai-player-card"
              style={{
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                marginBottom: '12px',
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '12px',
              }}>
                <span style={{
                  fontSize: '12px',
                  fontWeight: '700',
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}>
                  AI Recommendations
                </span>

                {audioLoading && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#6b7280' }}>
                    <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} />
                    Generating voice...
                  </div>
                )}

                {!audioLoading && (
                  <button
                    onClick={toggleAudio}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 12px',
                      backgroundColor: isPlaying ? '#ef4444' : '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '600',
                    }}
                  >
                    {isPlaying ? <><Pause size={14} />Pause</> : <><Volume2 size={14} />Play</>}
                  </button>
                )}
              </div>

              <p className="ai-player-advice-text">{analysis.advice}</p>
            </div>

            {/* Get New Advice Button */}
            <button
              onClick={getAdvice}
              disabled={loading}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: 'transparent',
                color: 'var(--primary-color)',
                border: '2px solid var(--primary-color)',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '600',
                marginBottom: '12px',
              }}
            >
              Refresh Analysis
            </button>

            {/* Quick Stats */}
            <div className="ai-stats-grid" style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: '8px',
              fontSize: '12px',
              marginBottom: '16px',
            }}>
              <div className="ai-player-stat-complete" style={{ padding: '10px', borderRadius: '6px', textAlign: 'center' }}>
                <div style={{ fontWeight: '700', fontSize: '18px' }}>
                  {analysis.complete?.length || analysis.filled?.length || 0}
                </div>
                <div style={{ fontWeight: '600', fontSize: '11px' }}>Complete</div>
              </div>
              <div className="ai-player-stat-partial" style={{ padding: '10px', borderRadius: '6px', textAlign: 'center' }}>
                <div style={{ fontWeight: '700', fontSize: '18px' }}>
                  {analysis.partiallyComplete?.length || 0}
                </div>
                <div style={{ fontWeight: '600', fontSize: '11px' }}>Partial</div>
              </div>
              <div className="ai-player-stat-incomplete" style={{ padding: '10px', borderRadius: '6px', textAlign: 'center' }}>
                <div style={{ fontWeight: '700', fontSize: '18px' }}>
                  {analysis.incomplete?.length || analysis.missing?.length || 0}
                </div>
                <div style={{ fontWeight: '600', fontSize: '11px' }}>Incomplete</div>
              </div>
            </div>

            {/* Incomplete Sections */}
            {(analysis.incomplete || analysis.missing) && (analysis.incomplete?.length > 0 || analysis.missing?.length > 0) && (
              <div className="ai-player-section-panel-incomplete">
                <div style={{
                  fontSize: '12px',
                  fontWeight: '700',
                  color: '#dc2626',
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}>
                  ✗ Incomplete Sections
                </div>
                <ul style={{ margin: 0, paddingLeft: '0', listStyle: 'none', fontSize: '13px', color: '#991b1b' }}>
                  {(analysis.incomplete || analysis.missing || []).map((field, index) => (
                    <SectionItem
                      key={index}
                      sectionName={field}
                      isExpanded={expandedSection === field}
                      onToggle={() => toggleSection(field)}
                      advice={sectionAdvice[field]}
                      audioLoading={sectionAudioLoading && expandedSection === field}
                      audioPlaying={sectionAudioPlaying === field}
                      onToggleAudio={() => toggleSectionAudio(field)}
                      loading={sectionLoading && expandedSection === field}
                      isComplete={false}
                    />
                  ))}
                </ul>
              </div>
            )}

            {/* Partially Complete Sections */}
            {analysis.partiallyComplete && analysis.partiallyComplete.length > 0 && (
              <div className="ai-player-section-panel-partial">
                <div style={{
                  fontSize: '12px',
                  fontWeight: '700',
                  color: '#b45309',
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}>
                  ◐ Partially Complete Sections
                </div>
                <ul style={{ margin: 0, paddingLeft: '0', listStyle: 'none', fontSize: '13px', color: '#92400e' }}>
                  {analysis.partiallyComplete.map((field, index) => (
                    <SectionItem
                      key={index}
                      sectionName={field}
                      isExpanded={expandedSection === field}
                      onToggle={() => toggleSection(field)}
                      advice={sectionAdvice[field]}
                      audioLoading={sectionAudioLoading && expandedSection === field}
                      audioPlaying={sectionAudioPlaying === field}
                      onToggleAudio={() => toggleSectionAudio(field)}
                      loading={sectionLoading && expandedSection === field}
                      isComplete={false}
                    />
                  ))}
                </ul>
              </div>
            )}

            {/* Complete Sections */}
            {(analysis.complete || analysis.filled) && (analysis.complete?.length > 0 || analysis.filled?.length > 0) && (
              <div className="ai-player-section-panel-complete">
                <div style={{
                  fontSize: '12px',
                  fontWeight: '700',
                  color: '#059669',
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}>
                  ✓ Complete Sections
                </div>
                <ul style={{ margin: 0, paddingLeft: '0', listStyle: 'none', fontSize: '13px', color: '#065f46' }}>
                  {(analysis.complete || analysis.filled || []).map((field, index) => (
                    <SectionItem
                      key={index}
                      sectionName={field}
                      isExpanded={expandedSection === field}
                      onToggle={() => toggleSection(field)}
                      advice={sectionAdvice[field]}
                      audioLoading={sectionAudioLoading && expandedSection === field}
                      audioPlaying={sectionAudioPlaying === field}
                      onToggleAudio={() => toggleSectionAudio(field)}
                      loading={sectionLoading && expandedSection === field}
                      isComplete={true}
                    />
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        @media (max-width: 640px) {
          .ai-advisor-modal {
            top: 10px !important;
            right: 10px !important;
            left: 10px !important;
            width: calc(100vw - 20px) !important;
            max-height: calc(100vh - 20px) !important;
          }
          .ai-stats-grid { gap: 6px !important; }
          .ai-stats-grid > div { padding: 8px 6px !important; }
          .ai-stats-grid > div > div:first-child { font-size: 16px !important; }
          .ai-stats-grid > div > div:last-child { font-size: 10px !important; }
        }

        @media (max-width: 480px) {
          .ai-advisor-modal h3 { font-size: 14px !important; }
          .ai-stats-grid {
            grid-template-columns: 1fr !important;
            gap: 8px !important;
          }
          .ai-stats-grid > div {
            display: flex !important;
            justify-content: space-between !important;
            align-items: center !important;
            padding: 10px 12px !important;
            text-align: left !important;
          }
          .ai-stats-grid > div > div:first-child { font-size: 20px !important; }
          .ai-stats-grid > div > div:last-child { font-size: 12px !important; }
        }
      `}</style>
    </div>
  );
};

export default PersistentAIPlayer;
